import {
  Badge,
  Button as MuiButton,
  IconButton as MuiIconButton,
  Tooltip,
} from "@mui/material";
import { ElementType } from "react";
import { OutstandingActionPing } from "./OutstandingActionPing";

type Btn = {
  dataTestId?: string;
  hasOutstandingAction?: boolean;
  isDisabled?: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  text?: string;
  endIcon?: React.ReactNode;
  startIcon?: React.ReactNode;
  tooltipContent?: React.ReactNode;
  variant?: "text" | "outlined" | "contained";
  textColor?: string;
  backgroundColor?: string;
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
  color?:
    | "inherit"
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning";
  size?: "small" | "medium" | "large";
  children?: React.ReactNode;
  component?: ElementType;
  showBadge?: boolean;
  badgeContent?: string;
  ariadescribedby?: string;
  ariacontrols?: string;
  ariaexpanded?: boolean;
  ariahaspopup?: "menu" | "listbox" | "dialog" | "grid" | "tree" | boolean;
  ariaLabel?: string;
  id?: string;
  openTooltip?: boolean;
  hideTooltip?: boolean;
  isIconButton?: boolean;
  className?: string;
};

const GenericWithTooltip = (props: Btn) => {
  return (
    <Tooltip
      title={props.tooltipContent}
      placement={props.tooltipPlacement}
      {...(props.openTooltip && { open: props.openTooltip })}
    >
      <div>
        <ButtonAlone {...props} />
      </div>
    </Tooltip>
  );
};
export const IconButton = (props: Btn) => (
  <MuiIconButton
    data-testid={props.dataTestId}
    onClick={(e: React.MouseEvent<HTMLButtonElement>) => props.onClick(e)}
    color={props.color}
    size={props.size}
    aria-describedby={props.ariadescribedby}
    disabled={props.isDisabled}
    aria-controls={props.ariacontrols}
    aria-expanded={props.ariaexpanded}
    aria-haspopup={props.ariahaspopup}
    aria-label={props.ariaLabel}
    id={props.id}
    className={`${props.className ?? ""} ${
      props.isDisabled ? "pointer-events-none" : ""
    }`}
  >
    <Badge
      color="error"
      invisible={props.showBadge}
      badgeContent={props.badgeContent}
    >
      {props.children}
    </Badge>
  </MuiIconButton>
);

export const ButtonAlone = (props: Btn) => {
  return !props.text ? (
    <IconButton
      showBadge={props.showBadge}
      badgeContent={props.badgeContent}
      onClick={props.onClick}
      size={props.size || "small"}
      color={props.color}
      ariadescribedby={props.ariadescribedby}
      isDisabled={props.isDisabled}
      ariacontrols={props.ariacontrols}
      ariaexpanded={props.ariaexpanded}
      ariahaspopup={props.ariahaspopup}
      ariaLabel={props.ariaLabel}
      id={props.id}
      dataTestId={props.dataTestId}
      className={props.className}
    >
      {props.children}
    </IconButton>
  ) : (
    <>
      <MuiButton
        id={props.id}
        data-testid={props.dataTestId}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => props.onClick(e)}
        size={props.size || "medium"}
        variant={props.variant}
        color={props.color}
        disabled={props.isDisabled}
        aria-describedby={props.ariadescribedby}
        aria-controls={props.ariacontrols}
        aria-expanded={props.ariaexpanded}
        aria-haspopup={props.ariahaspopup}
        aria-label={props.ariaLabel}
        endIcon={props.endIcon}
        startIcon={props.startIcon}
        component={props.component || "button"}
        className={`${props.className ?? ""} ${
          props.isDisabled ? "pointer-events-none" : ""
        }`}
        style={{
          color: props.textColor,
          backgroundColor: props.backgroundColor,
        }}
      >
        {props.text}
        {props.children}
      </MuiButton>
      {props.hasOutstandingAction && <OutstandingActionPing />}
    </>
  );
};

const defaultValues = {
  hasOutstandingAction: false,
  isDisabled: false,
  tooltipContent: "",
  tooltipPlacement: "bottom",
  color: "inherit",
  variant: "outlined",
} as const;

export const Button = (props: Btn) => {
  return (
    <div className="relative inline-flex">
      {props.tooltipContent && !props.hideTooltip ? (
        <GenericWithTooltip {...defaultValues} {...props} />
      ) : (
        <ButtonAlone {...defaultValues} {...props} />
      )}
    </div>
  );
};

export default Button;
